import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';

const News = ({ user, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/news/daily${forceRefresh ? '?refresh=true' : ''}`);
      setData(res.data);
      if (forceRefresh) toast.success('News refreshed');
    } catch {
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="dashboard-container" data-testid="news-page">
      <NavBar onLogout={onLogout} />
      <div className="dashboard-content page-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 className="page-title">
            <i className="fa-solid fa-newspaper" style={{ color: '#2c5364', marginRight: 10 }}></i>
            Dementia & Alzheimer's News
          </h1>
          <button
            className="news-refresh-btn"
            data-testid="news-refresh-btn"
            onClick={() => load(true)}
            disabled={refreshing || loading}
          >
            <i className={`fa-solid fa-rotate ${refreshing ? 'fa-spin' : ''}`}></i> {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <p className="page-subtitle">
          Curated daily from authoritative medical sources. Cached for 24 hours.
        </p>

        {loading && <div className="loading-screen">Loading today's news…</div>}

        {!loading && data && data.articles?.length === 0 && (
          <div className="directory-empty" data-testid="news-empty-state">
            <i className="fa-solid fa-newspaper directory-empty-icon"></i>
            <h3>No articles right now</h3>
            <p>We couldn't reach the news sources. Tap Refresh to try again.</p>
          </div>
        )}

        {!loading && data?.articles?.length > 0 && (
          <div className="news-grid">
            {data.articles.map((article, idx) => (
              <article
                className="news-card"
                key={article.link || idx}
                data-testid={`news-card-${idx}`}
              >
                <div className="news-card-meta">
                  <span className="news-source">{article.source}</span>
                  <span className="news-date">{formatDate(article.published_at)}</span>
                </div>
                <h3 className="news-title">{article.title}</h3>
                {article.ai_takeaway && (
                  <div className="news-takeaway" data-testid={`news-takeaway-${idx}`}>
                    <i className="fa-solid fa-wand-magic-sparkles"></i>
                    <span><strong>For caregivers:</strong> {article.ai_takeaway}</span>
                  </div>
                )}
                <p className="news-summary">{article.summary}</p>
                <a
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="news-read-more"
                  data-testid={`news-link-${idx}`}
                >
                  Read full article <i className="fa-solid fa-arrow-up-right-from-square"></i>
                </a>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default News;
